import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import cn from 'classnames';

import {
  excludedMealLabels,
} from '../../constants/labels';
import { getIngredients } from '../../actions/ingredient';
import { getProteins } from '../../actions/protein';
import DishComponent from './DishComponent';
import DishImage from './DishImage';
import DishVideo from './DishVideo';
import styles from './DishForm.module.css';

const defaultComponent = {
  fakeId: Date.now(),
  name: '',
  title: '',
  instruction: '',
  ingredients: [],
};

const DishForm = (props) => {
  const {
    getIngredients,
    ingredientsData,
    proteinsData,
    getProteins,
    initialValues = {},
    onSave,
  } = props;

  const [name, setName] = useState(initialValues?.name || '');
  const [mealName, setMealName] = useState(initialValues?.meal_name || '');
  const [cookingTime, setCookingTime] = useState(initialValues?.cooking_time || '');
  const [glutenFree, setGlutenFree] = useState(initialValues?.gluten_free || false);
  const [nutFree, setNutFree] = useState(initialValues?.nut_free || false);           // ← ADD
  const [mainProteinOverrideId, setMainProteinOverrideId] = useState(                 // ← ADD
    initialValues?.main_protein_override_id || ''
  );
  const [difficulty, setDifficulty] = useState(initialValues?.difficulty || '');
  const [serving, setServing] = useState(initialValues?.serving || '');
  const [components, setComponents] = useState(initialValues?.components || [defaultComponent]);
  const [image, setImage] = useState(initialValues?.image);
  const [video, setVideo] = useState(initialValues?.video);
  const [excludedMeals, setExcludeMeals] = useState({});

  useEffect(() => {
    getIngredients();
    getProteins();                                                                    // ← ADD
  }, []);

  useEffect(() => {
    if (initialValues && initialValues.excluded_meals?.length) {
      const excludedMeals = initialValues.excluded_meals.reduce((acc, item) => {
        acc[item.meal] = true;
        return acc;
      }, {});
      setExcludeMeals(excludedMeals);
    }
  }, [initialValues]);

  const changeExcludeMealHandler = (meal) => () => {
    const next = { ...excludedMeals };
    if (next[meal]) {
      delete next[meal];
    } else {
      next[meal] = true;
    }
    setExcludeMeals(next);
  };

  const collectExcludeMeals = (excludedMeals) => {
    return Object.keys(excludedMeals).map((meal) => ({ meal }));
  };

  const onSaveHandler = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return toast.error('Name is required', {
        position: 'bottom-right',
        duration: 3000,
      });
    }

    if (!mealName) {
      return toast.error('Meal name is required', {
        position: 'bottom-right',
        duration: 3000,
      });
    }

    if (!cookingTime) {
      return toast.error('Cooking time is required', {
        position: 'bottom-right',
        duration: 3000,
      });
    }

    if (!difficulty) {
      return toast.error('Difficulty is required', {
        position: 'bottom-right',
        duration: 3000,
      });
    }

    if (!image) {
      return toast.error('Pleas select dish image', {
        position: 'bottom-right',
        duration: 3000,
      });
    }

    // Reject duplicate component names within this dish.
    // The DB enforces UNIQUE (dish_id, name); MySQL's collation is case-insensitive,
    // so compare lowercased to match the backend behavior.
    const seenComponentNames = new Map();
    const hasDuplicateComponentName = components.some((component) => {
      const key = (component.name || '').trim().toLowerCase();
      if (!key) return false; // empty name handled by the loop below

      if (seenComponentNames.has(key)) return true;
      seenComponentNames.set(key, true);
      return false;
    });

    if (hasDuplicateComponentName) {
      return toast.error('Component names must be unique within a dish', {
        position: 'bottom-right',
        duration: 3000,
      });
    }

    // Check all data in components
    for (let component of components) {
      const { name, title, instruction, ingredients } = component;

      if (!name || !title || !instruction) {
        return toast.error('Component name, title, and instruction are required', {
          position: 'bottom-right',
          duration: 3000,
        });
      }

      if (!ingredients?.length) {
        return toast.error('Each component must have at least one ingredient', {
          position: 'bottom-right',
          duration: 3000,
        });
      }

      const missingAmount = ingredients.find(
        (i) =>
          i.unit_type !== 'without_amount' &&
          (i.amount === '' || i.amount === undefined || i.amount === null || Number(i.amount) <= 0)
      );

      if (missingAmount) {
        return toast.error(`Enter amount for "${missingAmount.name}"`, {
          position: 'bottom-right',
          duration: 3000,
        });
      }
    }

    const excludedMealsArray = collectExcludeMeals(excludedMeals);

    const dishData = {
      name: trimmedName,
      meal_name: mealName,
      image,
      video,
      cooking_time: cookingTime,
      difficulty,
      gluten_free: glutenFree,
      nut_free: nutFree,                                                              // ← ADD
      main_protein_override_id: mainProteinOverrideId || '',                          // ← ADD
      components,
      serving,
      excluded_meals: excludedMealsArray,
    };

    onSave(dishData);
  };

  const onChangeComponentHandler = (componentData, index) => {
    Object.assign(components[index], componentData);
    setComponents([...components]);
  };

  const addComponent = () => {
    const newComponent = {
      ...defaultComponent,
      fakeId: Date.now(),
    };

    components.push(newComponent);
    setComponents([...components]);
  };

  const deleteComponent = (index) => {
    components.splice(index, 1);
    setComponents([...components]);
  };

  const valueChangeHandler = (setter) => (event) => {
    setter(event.target.value);
  };

  const changeImageHandler = (imageData) => {
    setImage(imageData);
  };

  const changeVideoHandler = (videoData) => {
    setVideo(videoData);
  };

  const renderExcludedMeals = (excludeMeal) => {
    const { value, label } = excludeMeal;
    return (
      <div className={styles.excludedMealsRow}>
        <label key={value} className={styles.checkbox}>
          <span>{label}</span>
          <input
            checked={!!excludedMeals[value]}
            onChange={changeExcludeMealHandler(value)}
            type="checkbox"
          />
        </label>
      </div>
    );
  };

  return (
    <div>
      <label className={styles.row}>
        <span>Name of dish</span>
        <input
          className={styles.input}
          placeholder="Name of dish"
          value={name}
          onChange={valueChangeHandler(setName)}
        />
      </label>
      <label className={styles.row}>
        <span>Meal name</span>
        <select
          className={styles.select}
          onChange={valueChangeHandler(setMealName)}
          value={mealName}
        >
          <option value="" disabled>Select meal name</option>
          <option value="breakfast">Breakfast</option>
          <option value="snack">Snack</option>
          <option value="lunch">Lunch</option>
          <option value="dinner">Dinner</option>
        </select>
      </label>

      <label className={styles.row}>
        <span>Cooking time (min)</span>
        <input
          className={styles.input}
          placeholder="Cooking time (min)"
          value={cookingTime}
          type="number"
          onChange={valueChangeHandler(setCookingTime)}
        />
      </label>
      <label className={styles.row}>
        <span>Difficulty in preparing the dish</span>
        <select
          className={styles.select}
          onChange={valueChangeHandler(setDifficulty)}
          value={difficulty}
        >
          <option value="" disabled>Select difficulty</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </label>

      <label className={styles.checkbox}>
        <span>Gluten free</span>
        <input
          checked={glutenFree}
          onChange={({ target }) => setGlutenFree(target.checked)}
          type="checkbox"
        />
      </label>

      {/* ↓ NEW: Nut free checkbox */}
      <label className={styles.checkbox}>
        <span>Nut free</span>
        <input
          checked={nutFree}
          onChange={({ target }) => setNutFree(target.checked)}
          type="checkbox"
        />
      </label>

      {/* ↓ NEW: Main protein dropdown */}
      <label className={styles.row}>
        <span>Main protein</span>
        <select
          className={styles.select}
          onChange={valueChangeHandler(setMainProteinOverrideId)}
          value={mainProteinOverrideId}
        >
          <option value="">Auto-detect from ingredients</option>
          {proteinsData.map((proteinData) => (
            <option key={proteinData.id} value={proteinData.id}>
              {proteinData.name}
            </option>
          ))}
        </select>
      </label>

      <div className={styles.excludeMealsTitle}>This dish is for users who:</div>
      <div className={styles.excludeMeals}>
        {excludedMealLabels.map(renderExcludedMeals)}
      </div>

      <div className={styles.dishImages}>
        <div className={styles.dishImagesTitle}>
          Dish video
        </div>
        <DishVideo
          initialValues={video}
          onChange={changeVideoHandler}
        />
      </div>
      <div className={styles.dishImages}>
        <div className={styles.dishImagesTitle}>
          Dish image
        </div>
        <DishImage
          initialValues={image}
          onChange={changeImageHandler}
        />
      </div>
      <div className={styles.dishComponentsWrapper}>
        <div className={styles.dishComponents}>Dish Components</div>
        {components.map((component, index) => (
          <div className={styles.componentBlock} key={component.fakeId || component.id} >
            <DishComponent
              initialValues={component}
              onChange={componentData => onChangeComponentHandler(componentData, index)}
              ingredientsData={ingredientsData}
            />
            {components.length > 1 && (
              <button
                className={cn(styles.saveButton, 'button', 'red-button')}
                onClick={() => deleteComponent(index)}
              >
                Delete component
              </button>
            )}
          </div>
        ))}
        <button
          className={cn(styles.saveButton, 'button', 'green-button')}
          onClick={addComponent}
        >
          Add component
        </button>
      </div>
      <label className={styles.row}>
        <span>Serving</span>
        <textarea
          className={cn(styles.input, styles.serving)}
          placeholder="Serving"
          value={serving}
          onChange={valueChangeHandler(setServing)}
        />
      </label>

      <button
        className={cn(styles.saveButton, 'button', 'blue-button')}
        onClick={onSaveHandler}
      >
        Save
      </button>
    </div>
  );
};

const mapStateToProps = (store) => ({
  ingredientsData: store.ingredient.ingredientsData,
  proteinsData: store.protein.proteinsData,                                          // ← ADD
});

const mapDispatchToProps = {
  getIngredients,
  getProteins,                                                                        // ← ADD
};

export default connect(mapStateToProps, mapDispatchToProps)(DishForm);